import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { syberKeyMfaService } from '../services/syberKeyMfaService';
// import { Shield, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import './SyberKeyMFA.css';

const SyberKeyMFA: React.FC = () => {
  const [status, setStatus] = useState<{
    type: 'loading' | 'pending' | 'success' | 'error';
    message: string;
    data?: any;
  }>({ type: 'loading', message: 'Initiating SyberKey authentication...' });

  const [loginId, setLoginId] = useState<string>('');
  const [syberkeyUserId, setSyberkeyUserId] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const hasInitiated = useRef(false); // Prevent duplicate calls in React Strict Mode

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { createSession } = useAuth();

  useEffect(() => {
    // Prevent duplicate calls in React Strict Mode
    if (hasInitiated.current) {
      return;
    }
    hasInitiated.current = true;

    // Get user data from URL params or localStorage
    const email = searchParams.get('email') || localStorage.getItem('cognito_user_email') || '';
    const name = searchParams.get('name') || localStorage.getItem('cognito_user_name') || '';
    const userId = searchParams.get('userId') || '';

    setUserEmail(email);
    setUserName(name);
    setSyberkeyUserId(userId);

    if (!email || !name) {
      setStatus({
        type: 'error',
        message: 'Missing user information. Please try logging in again.'
      });
      return;
    }

    // Start SyberKey MFA flow
    initiateSyberKeyMFA(email, name, userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const initiateSyberKeyMFA = async (email: string, name: string, userId: string) => {
    try {
      console.log('🔄 [SyberKeyMFA] Starting MFA flow for:', { email, name, userId });
      setStatus({ type: 'loading', message: 'Checking user credentials...' });

      // Step 1: Check if user exists and is mapped to tenant
      const mappingResult = await syberKeyMfaService.checkUserTenantMapping(email);
      
      if (!mappingResult.success) {
        let errorMessage = mappingResult.message || 'User validation failed';
        
        // Provide specific guidance based on error type
        if (mappingResult.error === 'USER_NOT_FOUND') {
          errorMessage = 'User not found or has not shared credentials with MaxLife Corporation. Please visit https://appdemo.syberkey.com/ to register and share your credentials with MaxLife Corporation.';
        } else if (mappingResult.error === 'MAPPING_EXPIRED') {
          errorMessage = 'Your credential sharing has expired. Please visit https://appdemo.syberkey.com/ to share your credentials again with MaxLife Corporation.';
        } else if (mappingResult.error === 'NETWORK_ERROR') {
          errorMessage = 'Unable to connect to SyberKey service. Please check your internet connection and try again.';
        }
        
        setStatus({
          type: 'error',
          message: errorMessage
        });
        return;
      }

      // Step 2: User is mapped, now initiate authentication
      setStatus({ type: 'loading', message: 'Initiating SyberKey authentication...' });

      const authResult = await syberKeyMfaService.initiateAuth(mappingResult.syberkeyUserId!);
      setLoginId(authResult.loginId);

      setStatus({
        type: 'pending',
        message: 'Authentication request sent to SyberKey. Please approve on your mobile app.',
        data: authResult
      });

      // Start polling for approval using the service
      syberKeyMfaService.pollForApproval(
        authResult.loginId,
        (status) => handleStatusUpdate(status),
        (error) => handlePollingError(error)
      );

    } catch (error: any) {
      console.error('SyberKey MFA initiation error:', error);
      setStatus({
        type: 'error',
        message: error.message || 'Failed to initiate SyberKey authentication'
      });
    }
  };

  const openSyberKeyApp = () => {
    syberKeyMfaService.openMobileApp();
  };

  const handleStatusUpdate = async (status: any) => {
    if (status.status === 'approved') {
      setStatus({
        type: 'success',
        message: 'Authentication approved! Creating session...',
        data: status
      });
      
      // Create session with user data
      try {
        // Get the current user data from state or localStorage
        const currentEmail = userEmail || localStorage.getItem('cognito_user_email') || '';
        const currentName = userName || localStorage.getItem('cognito_user_name') || '';
        const currentUserId = syberkeyUserId || currentEmail;
        
        console.log('Creating session with user data:', {
          userId: currentUserId,
          email: currentEmail,
          fullName: currentName,
          loginId: loginId,
          tenantId: status.tenantId || 'maxlife_26cc36'
        });
        
        const user = {
          userId: currentUserId,
          email: currentEmail,
          fullName: currentName,
          loginId: loginId,
          tenantId: status.tenantId || 'maxlife_26cc36'
        };
        
        // Create session and wait for it to complete
        await createSession(user);
        
        setStatus({
          type: 'success',
          message: 'Authentication approved! Redirecting to dashboard...',
          data: status
        });
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          console.log('Redirecting to dashboard...');
          navigate('/dashboard');
        }, 2000);
      } catch (error) {
        console.error('Error creating session:', error);
        setStatus({
          type: 'error',
          message: 'Authentication approved but failed to create session. Please try again.'
        });
      }
    } else if (status.status === 'rejected' || status.status === 'denied') {
      setStatus({
        type: 'error',
        message: 'Authentication was denied. Please try again.'
      });
    }
  };

  const handlePollingError = (error: Error) => {
    console.error('Polling error:', error);
    setStatus({
      type: 'error',
      message: error.message || 'Authentication failed. Please try again.'
    });
  };


  const handleRetry = () => {
    navigate('/');
  };

  const getStatusIcon = () => {
    switch (status.type) {
      case 'loading':
        return <div className="w-8 h-8 text-blue-500 animate-spin">⟳</div>;
      case 'pending':
        return <div className="w-8 h-8 text-yellow-500 animate-pulse">⏰</div>;
      case 'success':
        return <div className="w-8 h-8 text-green-500">✓</div>;
      case 'error':
        return <div className="w-8 h-8 text-red-500">✗</div>;
      default:
        return <div className="w-8 h-8 text-gray-500">⚠</div>;
    }
  };

  const getStatusColor = () => {
    switch (status.type) {
      case 'loading':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'pending':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  return (
    <div className="syberkey-mfa-container">
      <div className="syberkey-mfa-card">
        {/* Header */}
        <div className="syberkey-mfa-header">
          <div className="syberkey-mfa-logo">
          <div className="syberkey-mfa-icon">
            <div className="w-10 h-10 text-white">🛡️</div>
          </div>
          </div>
          <h1 className="syberkey-mfa-title">SyberKey Authentication</h1>
          <p className="syberkey-mfa-subtitle">
            {userName ? `Welcome ${userName}!` : 'Secure authentication in progress...'}
          </p>
        </div>

        {/* Status Display */}
        <div className={`syberkey-mfa-status ${getStatusColor()}`}>
          <div className="syberkey-mfa-status-content">
            {getStatusIcon()}
            <div className="syberkey-mfa-status-text">
              <p className="syberkey-mfa-status-message">{status.message}</p>
              
              {status.type === 'pending' && (
                <div className="syberkey-mfa-pending-actions">
                  <p className="syberkey-mfa-instructions">Please check your SyberKey mobile app</p>
                  <div className="syberkey-mfa-waiting">
                    <span>⏰</span>
                    <span>Waiting for approval...</span>
                  </div>
                  <button
                    onClick={openSyberKeyApp}
                    className="syberkey-mfa-app-button"
                  >
                    Open SyberKey App
                  </button>
                </div>
              )}

              {status.type === 'loading' && (
                <div className="syberkey-mfa-loading-actions">
                  <p className="syberkey-mfa-loading-text">Setting up secure authentication...</p>
                  <button
                    onClick={openSyberKeyApp}
                    className="syberkey-mfa-app-button"
                  >
                    Open SyberKey App
                  </button>
                </div>
              )}

              {status.type === 'success' && (
                <div className="syberkey-mfa-success-actions">
                  <p className="syberkey-mfa-success-text">Authentication successful!</p>
                  <p className="syberkey-mfa-redirect-text">Redirecting to dashboard...</p>
                </div>
              )}

              {status.type === 'error' && (
                <div className="syberkey-mfa-error-actions">
                  <button
                    onClick={handleRetry}
                    className="syberkey-mfa-retry-button"
                  >
                    Try Again
                  </button>
                  {(status.message?.includes('register') || status.message?.includes('share')) && (
                    <button
                      onClick={() => window.open('https://appdemo.syberkey.com/', '_blank')}
                      className="syberkey-mfa-register-button"
                    >
                      Open SyberKey App
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Debug Info */}
        {process.env.NODE_ENV === 'development' && (
          <div className="syberkey-mfa-debug">
            <p><strong>Login ID:</strong> {loginId}</p>
            <p><strong>SyberKey User ID:</strong> {syberkeyUserId}</p>
            <p><strong>User:</strong> {userName} ({userEmail})</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyberKeyMFA;
